import { buildSectionSelector, normalizeSectionId, sectionManager, updateSection } from '@fc/sections';
import { isAbortError } from '@fc/section-response';
import { requestIdleCallback, onDocumentReady } from '@fc/utilities';

const HYDRATION_RETRY_MS = 250;

function getSectionElement(id) {
  return document.getElementById(buildSectionSelector(id));
}

function hasBlockingSurface(selector) {
  if (typeof selector !== 'string' || !selector.trim()) return false;
  return document.querySelector(selector) instanceof HTMLElement;
}

async function waitForHydrationWindow(selector) {
  while (true) {
    await new Promise((resolve) => requestIdleCallback(resolve));

    if (!hasBlockingSurface(selector)) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, HYDRATION_RETRY_MS));
  }
}

async function hydrateSection(id, options = {}) {
  if (typeof id !== 'string' || !id) return;

  const normalized = normalizeSectionId(id);
  const blockingSelector = options?.deferWhile;

  let element = getSectionElement(normalized);
  if (!element || element.dataset.hydrated === 'true') return;

  await waitForHydrationWindow(blockingSelector);

  let doc = null;
  try {
    doc = await sectionManager.getSectionDocument(normalized, { cache: false });
  } catch (error) {
    if (isAbortError(error) || isSectionFetchFailure(error)) return;
    throw error;
  }

  if (!doc) return;

  await waitForHydrationWindow(blockingSelector);

  element = getSectionElement(normalized);
  if (!element || element.dataset.hydrated === 'true') return;

  await updateSection(normalized, doc);
  getSectionElement(normalized)?.setAttribute('data-hydrated', 'true');

  if (document.getElementById('shopify-section-' + normalized)
    ?.querySelector('#fit-site-header')) {

    document.dispatchEvent(
      new CustomEvent('fit:header-hydrated', {
        detail: { sectionId: normalized }
      })
    );
  }
}

function isSectionFetchFailure(error) {
  return /^HTML fetch failed: \d+$/.test(String(error?.message ?? ''));
}

export async function hydrate(sectionId, options = {}) {
  if (typeof sectionId !== 'string' || !sectionId) return;
  onDocumentReady(() => requestIdleCallback(() => hydrateSection(sectionId, options)));
}
